import React, {
    useRef,
    useCallback,
    useMemo,
    useEffect,
    useState,
    Fragment,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useFormik } from "formik";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import queryParamsEntries from "common/query-params-entries";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppSearchInput from "components/app-search-input";
import addIcon from "assets/images/add-icon.svg";
import { formatDate } from "common/calendar";
import { formatTimeValue } from "common/time";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";
import exportIcon from "assets/images/pages/user-management/export-icon.svg";
import AppStatus from "components/app-status";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import AppCheckbox from "components/app-checkbox";
import ConfirmAppointmentModal from "components/pages/appointment/app-confirm-modal";
import CreateAppointmentModal from "components/pages/appointment/app-create-appointment-modal";
import AppCalendarInput from "components/app-calendar-input";
import { CALENDAR_FORMAT, isDateBeforeToday, isEndDateBeforeStartDate } from "common/calendar";
import AppSelectInput from "components/app-select-input";
import capitalizeCharacter from "common/capitalize-character";
import ERRORS from "common/errors";
import * as yup from "yup";
import placeholderFormat from "common/placeholder-format";

function PageAppointment() {
    const searchInputRef = useRef();
    const selectedRowRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const ConfirmAppointmentRef = useRef();
    const appointmentModalRef = useRef();
    const [anchorEl, setAnchorEl] = useState(null);
    const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;
    const [isExporting, setIsExporting] = useState(false);
    const [showAdvanceSearchForm, setShowAdvanceSearchForm] = useState(false);

    const filterDefaultStatus = useMemo(() => {
        const params = searchParams.get("statusListStr");
        return {
            upcoming: params?.includes("UPCOMING"),
            acknowledged: params?.includes("ACKNOWLEDGED"),
            completed: params?.includes("COMPLETED"),
            cancelled: params?.includes("CANCELLED"),
            overdue: params?.includes("OVERDUE"),
        };
    }, [searchParams]);
    const filterStatus = filterDefaultStatus;

    const paramsRef = useRef({
        page: parseInt(searchParams.get("page")) || 1,
        sort: searchParams.get("sort") || "",
        employeeName: searchParams.get("employeeName") || "",
        employeeNickname: searchParams.get("employeeNickname") || "",
        employeeEmail: searchParams.get("employeeEmail") || "",
        mentorName: searchParams.get("mentorName") || "",
        mentorNickname: searchParams.get("mentorNickname") || "",
        mentorEmail: searchParams.get("mentorEmail") || "",
        remark: searchParams.get("remark") || "",
        statusListStr: searchParams.get("statusListStr") || "",
        appointmentDateFrom: searchParams.get("appointmentDateFrom") || "",
        appointmentDateTo: searchParams.get("appointmentDateTo") || "",
    });

    const [data, setData] = useState({
        page: paramsRef.current.page,
        size: 10,
        total: 0,
        offset: 0,
        totalElements: 0,
        prev: false,
        next: false,
        items: [],
    });

    const onHandleTableMenu = (event, row) => {
        setAnchorEl(event.currentTarget);
        selectedRowRef.current = row;
    };

    const columns = useMemo(
        () => [
            {
                Header: "Employee Name",
                title: "Employee Name",
                accessor: "employeeName",
                disableSortBy: false,
                Cell: ({ row }) =>
                    <div className="employee-name-column">
                        <AppTableCell left value={row.original.employeeName} />
                    </div>
                ,
            },
            {
                Header: "Employee Nickname",
                title: "Employee Nickname",
                accessor: "employeeNickname",
                disableSortBy: false,
                Cell: ({ row }) => <AppTableCell left value={row.original.employeeNickname} />,
            },
            {
                Header: "Employee Email",
                title: "Employee Email",
                accessor: "employeeEmail",
                disableSortBy: false,
            },
            {
                Header: "Mentor Name",
                title: "Mentor Name",
                accessor: "mentorName",
                disableSortBy: false,
                className: "mentor-name-column",
                Cell: ({ row }) =>
                    <div className="mentor-name-column">
                        <AppTableCell left value={row.original.mentorName} />
                    </div>
                ,
            },
            {
                Header: "Mentor Nickname",
                title: "Mentor Nickname",
                accessor: "mentorNickname",
                disableSortBy: false,
                Cell: ({ row }) => <AppTableCell left value={row.original.mentorNickname} />,
            },
            {
                Header: "Mentor Email",
                title: "Mentor Email",
                accessor: "mentorEmail",
                disableSortBy: false,
            },
            {
                Header: "Appointment Date",
                title: "Appointment Date",
                accessor: "appointmentDate",
                disableSortBy: false,
                Cell: ({ row }) => (
                    <AppTableCell left value={formatDate(row.original.appointmentDate)} />
                ),
            },
            {
                Header: "Start Time",
                title: "Appointment Start Time",
                accessor: "appointmentStartTime",
                disableSortBy: false,
                Cell: ({ row }) => (
                    <AppTableCell left value={formatTimeValue(row.original.appointmentStartTime)} />
                ),
            },

            {
                Header: "End Time",
                title: "Appointment End Time",
                accessor: "appointmentEndTime",
                disableSortBy: false,
                Cell: ({ row }) => (
                    <AppTableCell left value={formatTimeValue(row.original.appointmentEndTime)} />
                ),
            },
            {
                Header: "Remark",
                title: "Remark",
                accessor: "remark",
                disableSortBy: false,
                className: "remark-column",
                Cell: ({ row }) =>
                    <div className="remark-column">
                        <AppTableCell left value={row.original.remark} />
                    </div>
                ,
            },
            {
                Header: "Status",
                title: "Status",
                accessor: "status",
                disableSortBy: false,
                Cell: ({ row }) =>
                    <AppStatus status={row.original.status} />
                ,
            },
            {
                Header: "",
                accessor: "*",
                disableSortBy: true,
                Cell: ({ row }) => (
                    <AppTableMoreIcon onClick={(event) => onHandleTableMenu(event, row.original.id)} />
                ),
            },

        ],
        []
    );

    const [visibleColumns, setVisibleColumns] = useState(
        columns
            .filter(c => defaultNoDisplayHeaders(c.accessor))
            .map(c => c.accessor)
    );

    const onHandleExport = useCallback(async () => {
        setIsExporting(true);

        const visibleColumnsFiltered = visibleColumns.filter((col) => col !== "*" && col !== "**");

        const visibleHeaders = columns.filter((col) => visibleColumnsFiltered.includes(col.accessor)).map((col) => col.title);

        const values = { ...paramsRef.current }

        let response = null;
        let fileName = null;

        try {
            const payload = {
                headers: visibleHeaders,
                columnFilters: visibleColumnsFiltered,
                searchFilters: {
                    employeeName: values.employeeName,
                    employeeNickname: values.employeeNickname,
                    employeeEmail: values.employeeEmail,
                    mentorName: values.mentorName,
                    mentorNickname: values.mentorNickname,
                    mentorEmail: values.mentorEmail,
                    remark: values.remark,
                    appointmentDateFrom: values.appointmentDateFrom,
                    appointmentDateTo: values.appointmentDateTo,
                    statuses: values.statusListStr,
                },
                sortFilter: values.sort,
            }

            const transformResponse = (data, headers) => {
                fileName = headers?.["content-disposition"]?.split("attachment; filename=")?.[1];

                if (fileName) return data;

                try {
                    const jsonResponse = JSON.parse(new TextDecoder().decode(data));
                    if (jsonResponse) return jsonResponse;
                } catch (error) {
                    return data;
                }
            };

            response = await api.post.appointment.export(payload, { transformResponse });
        } catch (error) {
            serveRequestErrors(error);
        } finally {
            setIsExporting(false);
        }

        if (response) {
            const a = document.createElement("a");
            document.body.appendChild(a);
            const url = window.URL.createObjectURL(new Blob([response]), { type: "application/octet-stream" });
            a.href = url;
            a.download = fileName;
            a.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }, 1000);
        }
    }, [visibleColumns, columns]);

    const onHandleShowAdvanceSearch = (event) => {
        setShowAdvanceSearchForm(true);
        if (searchInputRef.current?.onHandleShowAdvanceSearch) {
            searchInputRef.current.onHandleShowAdvanceSearch(event);
        }
    };

    const headerButtons = useMemo(() => {
        const buttons = [

            { label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch },
            { label: "Export", icon: exportIcon, outline: true, onClick: onHandleExport, disabled: isExporting, showLoadingDots: isExporting },
            {
                label: "Schedule",
                onClick: () => appointmentModalRef.current.onHandleShow(),
                icon: (
                    <img
                        src={addIcon}
                        className="page-appointment__add-icon"
                        alt="Add"
                    />
                ),
                outline: false
            }
        ];
        return buttons;
    }, [onHandleExport, isExporting]);


    const initialValues = useMemo(
        () => ({
            /* Employee fields */
            employeeId: "",
            employeeName: "",
            employeeNickname: "",
            employeeEmail: "",

            /* Mentor fields */
            mentorName: "",
            mentorNickname: "",
            mentorEmail: "",

            /* Appointment date range */
            appointmentDateFrom: "",
            appointmentDateTo: "",

            /* Status */
            statusListStr: "",
        }),
        [],
    );

    const formik = useFormik({
        initialValues,
        validationSchema: yup.object({
            appointmentDateFrom: yup.string().test("appointmentDateFrom", ERRORS.START_DATE, function (value) {
                if (!value || !this.parent.appointmentDateTo)
                    return true;

                return isDateBeforeToday(value, this.parent.appointmentDateTo);
            }),
            appointmentDateTo: yup.string().test("appointmentDateTo", ERRORS.END_DATE, function (value) {
                if (!value || !this.parent.appointmentDateFrom)
                    return true;

                return isDateBeforeToday(this.parent.appointmentDateFrom || new Date(), value);
            }),
            startDate: yup.string().test("startDate", ERRORS.START_DATE, function (value) {
                if (!value || !this.parent.startDate)
                    return true;

                return isDateBeforeToday(value, this.parent.startDate);
            }),
        }),
        onSubmit: (values) => {
            onHandleSubmitSearch(values);
        },
    });

    //prettier-ignore
    const onHandleAppointmentDateFrom = useCallback((name, value) => {
        formik.setFieldValue(name, value);

        if (isEndDateBeforeStartDate(value, formik.values.appointmentDateTo)) formik.setFieldValue("appointmentDateTo", "");
    }, [formik]);

    const setValues = useMemo(() => formik.setValues, [formik]);

    const advanceSearchValues = useMemo(() => {
        const params = queryParamsEntries(searchParams);
        return {
            name: params.name,
            nickname: params.nickname,
            employeeId: params.employeeId,
            claimType: params.claimType,
            email: params.email,
            statusListStr: capitalizeCharacter(params.statusListStr?.split("_")?.join(" ")),
            createdStartDate: params.createdStartDate,
            createdEndDate: params.createdEndDate,
            startDate: params.startDate,
            endDate: params.endDate
        };
    }, [searchParams]);

    /* ----------------- helpers ----------------- */
    const onHandleRemoveField = (field) => {
        formik.setFieldValue(field, "");
        paramsRef.current = { ...paramsRef.current, page: 1, [field]: "" };
        onHandleGetList();
    };

    const onHandleResetSearch = () => {
        formik.setValues(formik.initialValues);
        paramsRef.current = { page: 1, sort: "", ...formik.initialValues };
        onHandleGetList();
    };

    const onHandleSubmitSearch = (values) => {
        setShowAdvanceSearchForm(false);
        searchInputRef.current.onhandleCloseAdvanceSearch();
        paramsRef.current = { ...paramsRef.current, page: 1, ...values };
        onHandleGetList();
    };

    /* ----------------- load data ----------------- */
    const onHandleGetList = useCallback(async () => {
        let response = null;
        setData({
            page: paramsRef.current.page,
            size: 10,
            total: 0,
            offset: 0,
            totalElements: 0,
            prev: false,
            next: false,
            items: [],
        });

        try {
            const payload = { ...paramsRef.current, size: 10 };
            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });
            payload.page = paramsRef.current.page - 1;
            if (payload.appointmentDateFrom) {
                payload.appointmentDateFrom = new Date(payload.appointmentDateFrom)
                    .toISOString()
                    .slice(0, 10);
            }

            if (payload.appointmentDateTo) {
                payload.appointmentDateTo = new Date(payload.appointmentDateTo)
                    .toISOString()
                    .slice(0, 10);
            }


            response = await api.get.appointment.list(sanitizeObject(payload));
        } catch (error) {
            serveRequestErrors(error);
        }

        setData((prev) => ({
            ...prev,
            page: paramsRef.current.page,
            prev: response ? !response.first : false,
            next: response ? !response.last : false,
            items: response?.content ?? [],
            total: response ? response.totalPages : 1,
            totalElements: response ? response.totalElements : response?.content?.length || 0,
            offset: response?.pageable?.offset || 0,
            status: response?.pageable,
        }));
    }, []);

    /* ----------------- table callbacks ----------------- */
    const onHandlePagination = useCallback(
        (event) => {
            const ctrl = event.currentTarget?.getAttribute("data-ctrl");
            if (ctrl === "prev" && paramsRef.current.page > 1) {
                paramsRef.current.page -= 1;
            } else if (ctrl === "next" && paramsRef.current.page < data.total) {
                paramsRef.current.page += 1;
            } else if (!ctrl) {
                paramsRef.current.page = event.target.value;
            }
            onHandleGetList();
        },
        [onHandleGetList, data.total]
    );

    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";

        paramsRef.current.page = 1;

        onHandleGetList();
    }, [onHandleGetList]);


    const onhandleCloseMenu = useCallback(() => {
        const el = document.querySelector(".table__sort");
        const filtered = Object.values(filterStatus).some(Boolean);
        el?.classList.toggle("table__sort--active", filtered);
        setAnchorEl(null);
    }, [filterStatus]);

    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        const { page, sort, ...res } = paramsRef.current;
        setValues((prev) => ({ ...prev, ...res }));
    }, [setValues]);

    const toggleColumnVisibility = (accessor) => {
        setVisibleColumns((prev) => {
            if (!prev.includes(accessor)) return [...prev, accessor];

            const next = prev.filter((val) => val !== accessor);
            const hasDataColumn = next.some((val) => val && val !== "*" && val !== "**");

            return hasDataColumn ? next : prev;
        });
    };

    // const AddNewButton = [
    //     {
    //         label: " Schedule",
    //         icon: addIcon,
    //         onClick: () => appointmentModalRef.current.onHandleShow()
    //     }
    // ];
    
    function defaultNoDisplayHeaders(col) {
        if (col === "mentorNickname") return false;
        if (col === "employeeNickname") return false;
        if (col === "mentorEmail") return false;
        if (col === "employeeEmail") return false;
        return true;
    }

    return (
        <div className="page-appointment-history">
            <div className="company-appointment-history">
                <div className="app-appointment-history">

                    {/* {(
                        <AppEmptyState
                            title="No records found"
                            description="You don’t have any records yet"
                            buttons={AddNewButton}
                        />
                    )} */}
                    {(
                        <Fragment>
                            <AppSearchInput
                                ref={searchInputRef}
                                multiValues={advanceSearchValues}
                                onRemoveField={onHandleRemoveField}
                                buttons={headerButtons}
                            >
                                {showAdvanceSearchForm && (
                                    <form
                                        className="app-advance-search-form"
                                        onSubmit={formik.handleSubmit}
                                    >
                                        <div className="advance-form">
                                            <div className="advance-form__inputs">
                                                <span className="advance-form__inputs__toggle-word">
                                                    Select Columns to Display:
                                                </span>
                                                <div className="advance-form__inputs__checkbox">
                                                    {columns
                                                        .filter((col) => col.accessor && col.accessor !== "*" && col.accessor !== "**")
                                                        .map((col) => (
                                                            <div className="checkbox-wrapper" key={col.accessor}>
                                                                <AppCheckbox
                                                                    name={col.accessor}
                                                                    value={visibleColumns.includes(col.accessor)}
                                                                    onClick={() => toggleColumnVisibility(col.accessor)}
                                                                    label={col.title}
                                                                />
                                                            </div>
                                                        ))}
                                                </div>

                                                <AppInput
                                                    type="text"
                                                    name="employeeName"
                                                    label="Employee Name"
                                                    placeholder="Enter employee name"
                                                    value={formik.values.employeeName}
                                                    onChange={formik.handleChange}
                                                />

                                                <AppInput
                                                    type="text"
                                                    name="employeeNickname"
                                                    label="Employee Nickname"
                                                    placeholder="Enter employee nickname"
                                                    value={formik.values.employeeNickname}
                                                    onChange={formik.handleChange}
                                                />

                                                <AppInput
                                                    type="text"
                                                    name="employeeEmail"
                                                    label="Employee Email"
                                                    placeholder="Enter employee email"
                                                    value={formik.values.employeeEmail}
                                                    onChange={formik.handleChange}
                                                />

                                                <AppInput
                                                    type="text"
                                                    name="mentorName"
                                                    label="Mentor Name"
                                                    placeholder="Enter mentor name"
                                                    value={formik.values.mentorName}
                                                    onChange={formik.handleChange}
                                                />

                                                <AppInput
                                                    type="text"
                                                    name="mentorNickname"
                                                    label="Mentor Nickname"
                                                    placeholder="Enter mentor nickname"
                                                    value={formik.values.mentorNickname}
                                                    onChange={formik.handleChange}
                                                />

                                                <AppInput
                                                    type="text"
                                                    name="mentorEmail"
                                                    label="Mentor Email"
                                                    placeholder="Enter mentor email"
                                                    value={formik.values.mentorEmail}
                                                    onChange={formik.handleChange}
                                                />

                                                <AppSelectInput
                                                    name="statusListStr"
                                                    label="Status"
                                                    placeholder="Select Appointment Status"
                                                    options={COMMON.DROPDOWNS.APPOINTMENT}
                                                    value={formik.values.statusListStr}
                                                    onChange={formik.handleChange}
                                                />

                                                <AppCalendarInput
                                                    name="appointmentDateFrom"
                                                    label="Date From"
                                                    placeholder={placeholderFormat}
                                                    displayFormat={calendarDisplayFormat}
                                                    value={formik.values.appointmentDateFrom}
                                                    error={formik.errors.appointmentDateFrom}
                                                    touched={formik.touched.appointmentDateFrom}
                                                    onChange={onHandleAppointmentDateFrom}
                                                />

                                                <AppCalendarInput
                                                    name="appointmentDateTo"
                                                    label="Date To"
                                                    placeholder={placeholderFormat}
                                                    displayFormat={calendarDisplayFormat}
                                                    minDate={formik.values.appointmentDateFrom}
                                                    value={formik.values.appointmentDateTo}
                                                    error={formik.errors.appointmentDateTo}
                                                    touched={formik.touched.appointmentDateTo}
                                                    onChange={formik.setFieldValue}
                                                />
                                            </div>

                                            <div className="advance-form__button-container">
                                                <AppButton
                                                    type="button"
                                                    label="Clear"
                                                    outline
                                                    onClick={onHandleResetSearch}
                                                />
                                                <AppButton
                                                    type="submit"
                                                    label="Search"
                                                />
                                            </div>

                                        </div>
                                    </form>
                                )}
                            </AppSearchInput>

                            {(
                                paramsRef.current.employeeId ||
                                paramsRef.current.employeeName ||
                                paramsRef.current.employeeNickname ||
                                paramsRef.current.employeeEmail ||
                                paramsRef.current.mentorName ||
                                paramsRef.current.mentorNickname ||
                                paramsRef.current.mentorEmail ||
                                paramsRef.current.appointmentDateFrom ||
                                paramsRef.current.appointmentDateTo ||
                                paramsRef.current.statusListStr
                            ) && (
                                    <div className="appointment-history__results">
                                        <p className="appointment-history__text">
                                            {data.totalElements} results found
                                        </p>
                                    </div>
                                )}
                            <AppTable
                                pages={data}
                                onHandlePagination={onHandlePagination}
                                onHandleSort={onHandleSort}
                                columns={columns.filter(col => visibleColumns.includes(col.accessor))}
                            />

                            <Menu
                                classes={{ root: "app-table-menu" }}
                                anchorEl={anchorEl}
                                open={!!anchorEl}
                                onClose={onhandleCloseMenu}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        ConfirmAppointmentRef.current.onHandleShow(selectedRowRef.current, "COMPLETED")
                                        setAnchorEl(null);
                                    }}
                                >
                                    Mark as Completed
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        ConfirmAppointmentRef.current.onHandleShow(selectedRowRef.current, "CANCELLED")
                                        setAnchorEl(null);
                                    }}
                                >
                                    Mark as Cancelled
                                </MenuItem>
                            </Menu>
                        </Fragment>
                    )}

                    <ConfirmAppointmentModal
                        ref={ConfirmAppointmentRef}
                        onHandleGetList={onHandleGetList}
                    />

                    <CreateAppointmentModal onHandleGetList={onHandleGetList} ref={appointmentModalRef} />

                </div>
            </div>
        </div>
    );
}

export default PageAppointment;
